import type { StateCreator } from "zustand"

import { withTokenizedWords } from "@/components/SyncScreen/wordSync"
import type { AppState, LyricsSlice } from "@/store/types"
import type { LyricLine } from "@/types"

const HISTORY_LIMIT = 50

let idCounter = 10

function clone(lyrics: LyricLine[]): LyricLine[] {
  return structuredClone(lyrics)
}

function pushHistory(
  history: LyricLine[][],
  idx: number,
  next: LyricLine[],
): { history: LyricLine[][]; idx: number } {
  const trimmed = history.slice(0, idx + 1)
  trimmed.push(clone(next))
  while (trimmed.length > HISTORY_LIMIT) trimmed.shift()
  return { history: trimmed, idx: trimmed.length - 1 }
}

export const createLyricsSlice: StateCreator<AppState, [], [], LyricsSlice> = (
  set,
) => ({
  lyrics: [
    { id: "1", text: "", time: null, hookWords: "", words: [] },
    { id: "2", text: "", time: null, hookWords: "", words: [] },
    { id: "3", text: "", time: null, hookWords: "", words: [] },
  ],
  lyricsHistory: [],
  lyricsHistoryIdx: -1,

  setLyrics: (lyrics, skipHistory = false) =>
    set((s) => {
      const next = clone(lyrics)
      if (skipHistory) return { lyrics: next }
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  updateLyric: (id, patch) =>
    set((s) => {
      const next = s.lyrics.map((l) => {
        if (l.id !== id) return l
        const merged = { ...l, ...patch }
        if (patch.text !== undefined && patch.text !== l.text) {
          return withTokenizedWords(merged)
        }
        return merged
      })
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  addLyric: () =>
    set((s) => {
      const next = [
        ...s.lyrics,
        {
          id: String(++idCounter),
          text: "",
          time: null,
          hookWords: "",
          words: [],
        },
      ]
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  removeLyric: (id) =>
    set((s) => {
      const next = s.lyrics.filter((l) => l.id !== id)
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  stampTime: (id, time) =>
    set((s) => {
      const next = s.lyrics.map((l) => (l.id === id ? { ...l, time } : l))
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  reorderLyrics: (from, to) =>
    set((s) => {
      const len = s.lyrics.length
      if (len === 0) return {}
      const f = Math.max(0, Math.min(len - 1, from))
      const t = Math.max(0, Math.min(len - 1, to))
      if (f === t) return {}
      const next = s.lyrics.slice()
      const [moved] = next.splice(f, 1)
      next.splice(t, 0, moved)
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  toggleLock: (id) =>
    set((s) => {
      const next = s.lyrics.map((l) =>
        l.id === id ? { ...l, locked: !l.locked } : l,
      )
      const { history, idx } = pushHistory(
        s.lyricsHistory,
        s.lyricsHistoryIdx,
        next,
      )
      return { lyrics: next, lyricsHistory: history, lyricsHistoryIdx: idx }
    }),

  undo: () =>
    set((s) => {
      if (s.lyricsHistoryIdx <= 0) return {}
      const idx = s.lyricsHistoryIdx - 1
      return { lyrics: clone(s.lyricsHistory[idx]), lyricsHistoryIdx: idx }
    }),

  redo: () =>
    set((s) => {
      if (s.lyricsHistoryIdx >= s.lyricsHistory.length - 1) return {}
      const idx = s.lyricsHistoryIdx + 1
      return { lyrics: clone(s.lyricsHistory[idx]), lyricsHistoryIdx: idx }
    }),
})
